// src/components/Directory/SummaryReportsTable.jsx
import React, { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from 'react';
import axios from 'axios';
import './DirectoryTable.css';
import { FaDownload, FaTrash, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import API_BASE_URL from '../../config';

const STORAGE_SORT_KEY = 'SummaryReportsTableSort';
const STORAGE_SCROLL_KEY = 'SummaryReportsTableScroll';


const SummaryReportsTable = ({ refreshKey, query }) => {
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_SORT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return { key: parsed.key, direction: parsed.direction || 'asc' };
      }
    } catch {}
    return { key: null, direction: 'asc' };
  });
  const tableWrapperRef = useRef(null);
  const rowRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch reports
  useEffect(() => {
    setLoading(true);
    const fetchReports = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/report/get_all_report`);
        setRows(data);
      } catch (err) {
        console.error('Ошибка загрузки отчётов:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, [refreshKey]);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditValue('');
  }, []);

  const saveEdit = useCallback(
    async id => {
      try {
        await axios.put(
         `${API_BASE_URL}/report/update_report_name/${id}`,
          { name: editValue }
        );
        setRows(prev =>
          prev.map(r => (r.id === id ? { ...r, name: editValue } : r))
        );
        cancelEdit();
      } catch (err) {
        console.error('Ошибка обновления имени отчёта:', err);
      }
    },
    [editValue, cancelEdit]
  );

  // Auto-save on click outside
  useEffect(() => {
    if (!editingId) return;
    const handleClickOutside = e => {
      if (rowRef.current && !rowRef.current.contains(e.target)) {
        saveEdit(editingId);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editingId, saveEdit]);

  // Auto-resize input
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.width = 'auto';
      inputRef.current.style.width = `${inputRef.current.scrollWidth}px`;
    }
  }, [editingId, editValue]);

  // Сохраняем состояние сортировки в sessionStorage при его изменении
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_SORT_KEY, JSON.stringify(sortConfig));
    } catch {}
  }, [sortConfig]);

  // Восстановление прокрутки таблицы при монтировании
  useEffect(() => {
    try {
      const savedScroll = sessionStorage.getItem(STORAGE_SCROLL_KEY);
      if (savedScroll && tableWrapperRef.current) {
        tableWrapperRef.current.scrollTop = Number(savedScroll);
      }
    } catch {}
  }, []);

  

  // Фильтрация по query (по дате и названию отчёта)
  const filteredRows = useMemo(() => {
    if (loading) return [];
    const q = (query || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(({ date, name, file_url }) => {
      const formattedDate = (() => {
        try {
          return new Date(date).toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
        } catch {
          return date;
        }
      })();
      const title = name || file_url.split('/').pop();
      return (
        String(formattedDate).toLowerCase().includes(q) ||
        String(title).toLowerCase().includes(q)
      );
    });
  }, [rows, query, loading]);

  // Сортировка по ключу
  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return filteredRows;
    const arr = [...filteredRows].sort((a, b) => {
      let aV, bV;
      if (sortConfig.key === 'date') {
        aV = a.date ? new Date(a.date).getTime() : 0;
        bV = b.date ? new Date(b.date).getTime() : 0;
      } else if (sortConfig.key === 'name') {
        const aTitle = a.name || a.file_url.split('/').pop();
        const bTitle = b.name || b.file_url.split('/').pop();
        aV = String(aTitle).toLowerCase();
        bV = String(bTitle).toLowerCase();
      } else {
        aV = '';
        bV = '';
      }
      if (aV < bV) return -1;
      if (aV > bV) return 1;
      return 0;
    });
    return sortConfig.direction === 'asc' ? arr : arr.reverse();
  }, [filteredRows, sortConfig]);

  // Восстановление прокрутки при изменении отсортированного списка
  useLayoutEffect(() => {
    try {
      const savedScroll = sessionStorage.getItem(STORAGE_SCROLL_KEY);
      if (savedScroll && tableWrapperRef.current) {
        tableWrapperRef.current.scrollTop = Number(savedScroll);
      }
    } catch {}
  }, [sortedRows]);

  // Обработчик прокрутки: сохраняем позицию
  const handleScroll = () => {
    if (tableWrapperRef.current) {
      try {
        sessionStorage.setItem(
          STORAGE_SCROLL_KEY,
          tableWrapperRef.current.scrollTop.toString()
        );
      } catch {}
    }
  };

  // Обработчик сортировки по столбцу
  const handleSort = key => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  // Действия с отчётом
  const formatDate = iso => {
    try {
      return new Date(iso).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  const downloadReport = async (id, fileUrl) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/report/download_report/${id}`,
        { responseType: 'blob' }
      );
      let filename =
        res.headers['content-disposition']?.match(/filename="?(.+?)"?/)?.[1] ||
        fileUrl.split('/').pop();
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Ошибка скачивания отчёта:', err);
    }
  };

  const deleteReport = async id => {
    try {
      await axios.delete(`${API_BASE_URL}/report/delete_report/${id}`);
      setRows(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      console.error('Ошибка удаления отчёта:', err);
    }
  };

  const startEdit = (id, currentName) => {
    setEditingId(id);
    setEditValue(currentName);
  };

  if (loading) {
    return <div className="table-container">Загрузка отчётов…</div>;
  }

  return (
    <div className="table-container">
      <div
        className="table-wrapper"
        ref={tableWrapperRef}
        onScroll={handleScroll}
      >
        <table className="custom-table hov">
          <colgroup>
            <col style={{ width: '15%' }} />
            <col />
            <col style={{ width: '120px' }} />
          </colgroup>
          <thead>
            <tr>
              <th
                className="date-col sortable-header"
                onClick={() => handleSort('date')}
              >
                Дата
              </th>
              <th
                className="sortable-header"
                onClick={() => handleSort('name')}
              >
                Наименование отчёта
              </th>
              <th className="date-col" />
            </tr>
          </thead>
          <tbody>
            {sortedRows.length > 0 ? (
              sortedRows.map(({ id, date, file_url, name }) => {
                const title = name || file_url.split('/').pop();
                const editing = editingId === id;
                return (
                  <tr
                    key={id}
                    ref={editing ? rowRef : null}
                    className={editing ? 'editing-row' : ''}
                  >
                    <td className="date-col" style={{ fontSize: '14px' }}>
                      {`Отчёт от ${formatDate(date)}`}
                    </td>
                    <td className="name-cell">
                      {editing ? (
                        <input
                          type="text"
                          className="edit-input"
                          value={editValue}
                          ref={inputRef}
                          autoFocus
                          onChange={e => setEditValue(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && saveEdit(id)}
                        />
                      ) : (
                        <>
                          {title}
                          <FaEdit
                            className="edit-icon-inline"
                            title="Переименовать"
                            onClick={() => startEdit(id, title)}
                          />
                        </>
                      )}
                    </td>
                    <td className="date-col icon-cell">
                      {editing ? (
                        <>
                          <FaSave
                            className="icon-btn-action"
                            title="Сохранить"
                            onClick={() => saveEdit(id)}
                          />
                          <FaTimes
                            className="icon-btn-action"
                            title="Отменить"
                            onClick={cancelEdit}
                          />
                        </>
                      ) : (
                        <>
                          <FaDownload
                            className="icon-btn-action"
                            title="Скачать"
                            onClick={() => downloadReport(id, file_url)}
                          />
                          <FaTrash
                            className="icon-btn-action"
                            title="Удалить"
                            onClick={() => deleteReport(id)}
                          />
                        </>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="3" style={{ textAlign: 'center' }}>
                  Нет доступных отчётов
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SummaryReportsTable;
